import React, { useState } from 'react';
import _ from 'lodash';
import { CashRegisterTotals, useButton, useCurrency, useI18n, useSelector } from '~/Hooks';
import { CountAndAmount, SerializedStorage as ZReport } from '@baemingo/report-events/dist/types';
import { useStorage } from '~Components/Storage';
import { TranslatedString } from '~Types';
import { GetVenue_venue_company_staffs } from '../__queries__';

import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import {
  Wrapper,
  StaffDropdown,
  TableWrapper,
  Table,
  Info,
  HeaderRow,
  Row,
  Text,
  InfoText,
  OrgInfo,
} from './Styles';
import { aggregateCategorySales } from '~utilities';
import { getNorwaySpecificVariables, DisplayAggregatedCategories, getStaffName } from '../utils';
import { DateTime } from 'luxon';
import { BlueButton } from '~Styles';
import styled from '@emotion/styled';
import NoReport from '../Components/NoReport';

const PrintButton = styled(BlueButton)`
  text-transform: none;
`;

interface Props {
  data: any[] | undefined;
  orgId: string;
  companyName: string;
  country: string;
  handlePrint: (report: any) => void;
  cashRegister: CashRegisterTotals | null;
  showVenueReport: boolean;
  isXReport: boolean;
  categories: {
    id: string;
    nameLang: TranslatedString;
  }[];
  staffs: GetVenue_venue_company_staffs[];
  payouts: any;
  openCheques: CountAndAmount | null;
}

const NorwayView: React.FunctionComponent<Props> = ({
  data,
  orgId,
  companyName,
  country,
  handlePrint,
  cashRegister,
  showVenueReport,
  isXReport,
  categories,
  staffs,
  payouts,
  openCheques,
}) => {
  const { format } = useCurrency();
  const { i18n } = useI18n();

  const companyTimeZone = useSelector((state) => state.companyTimeZone);
  const [reportToShow, setReportToShow] = useState<ZReport | undefined>(
    data ? data[data.length - 1] : undefined,
  );
  const [storage] = useStorage();

  const aggregatedCategories = reportToShow
    ? aggregateCategorySales(categories, reportToShow.categoryTotals, i18n)
    : {};

  const printButton = useButton({
    onClick: () => handlePrint(reportToShow),
  });

  // If we have no reports in the ZReports array, break early.
  if (!data || data.length === 0) {
    return <NoReport />;
  }

  const multipleReports = data.length > 1;

  const { linesEntered, turnover } = getNorwaySpecificVariables(reportToShow);

  return (
    <Wrapper>
      {multipleReports && (
        <StaffDropdown onChange={(e) => setReportToShow(data[Number(e.target.value)])}>
          {data.map((report, index) => {
            return (
              <option selected={index === data.length - 1} value={index} key={report.sequenceId}>
                {index === data.length - 1 && isXReport
                  ? i18n('Report.XReport')
                  : report.sequenceId +
                    ' - ' +
                    DateTime.fromISO(report.date)
                      .setZone(companyTimeZone)
                      .toFormat('yyyy-MM-dd HH:mm')}
              </option>
            );
          })}
        </StaffDropdown>
      )}
      {reportToShow && (
        <TableWrapper>
          <Table>
            <Info>
              {!isXReport && (
                <p>
                  {i18n('Report.SequenceID')}: {reportToShow.sequenceId}
                </p>
              )}
              <p>
                {i18n('Report.RegisterID')}:{' '}
                {showVenueReport ? 'Alla' : storage.taxComplianceDeviceId}
              </p>
            </Info>

            <HeaderRow>
              <Text>{i18n('Report.TotalSales')}</Text>
              <Text>{i18n('Report.Qty')}</Text>
              <Text>{i18n('General.Sum')}</Text>
            </HeaderRow>
            <Row>
              <Text>{i18n('Report.TotalSales')}</Text>
              <Text>{reportToShow.numberOfOrders}</Text>
              <Text>{format(reportToShow.totalSales)}</Text>
            </Row>

            <HeaderRow>
              <Text>{i18n('Report.CategorySales')}</Text>
              <Text>{i18n('General.Quantity')}</Text>
              <Text>{i18n('General.Sum')}</Text>
            </HeaderRow>

            <DisplayAggregatedCategories
              categoriesToDisplay={aggregatedCategories}
              formatCurrency={format}
              i18n={i18n}
            />

            <HeaderRow>
              <Text>{i18n('Report.NumberOfItems')}</Text>
              <Text>{i18n('General.Quantity')}</Text>
            </HeaderRow>
            <Row>
              <Text>{i18n('Report.NumberOfItemsSold')}</Text>
              <Text>{reportToShow.numberOfItems}</Text>
            </Row>
            <Row>
              <Text>{i18n('Report.NumberOfItemsRefunded')}</Text>
              <Text>{reportToShow.numberOfItemsRefunded}</Text>
            </Row>

            <HeaderRow>
              <Text>{i18n('Report.VATSpec')}</Text>
              <Text></Text>
              <Text>{i18n('General.Sum')}</Text>
            </HeaderRow>
            {_.map(reportToShow.vatTotals, (amount, name) => (
              <Row key={name}>
                <Text>
                  {i18n('ZReport.VAT')} {name}%
                </Text>
                <Text></Text>
                <Text>{format(amount)}</Text>
              </Row>
            ))}
            <Row>
              <Text>
                <strong>{i18n('Report.SumVATTotal')}</strong>
              </Text>
              <Text>
                <strong>{format(reportToShow.vatTotalAmount)}</strong>
              </Text>
            </Row>

            <HeaderRow>
              <Text>{i18n('Report.NetSales')}</Text>
              <Text></Text>
              <Text>{i18n('General.Sum')}</Text>
            </HeaderRow>
            {_.map(reportToShow.saleForVatTotals, (amount: number, name: number) => (
              <Row key={name}>
                <Text>
                  {i18n('Report.NetSales')} {name}%
                </Text>
                <Text></Text>
                <Text>{format(amount - reportToShow.vatTotals[name])}</Text>
              </Row>
            ))}

            <HeaderRow>
              <Text>{i18n('Report.PaymentMethods')}</Text>
              <Text>{i18n('General.Quantity')}</Text>
              <Text>{i18n('General.Sum')}</Text>
            </HeaderRow>
            {_.map(reportToShow.paymentMethodTotals, (method, name) => (
              <Row key={name}>
                <Text>
                  {name.toLowerCase() === 'adyen' || name.toLowerCase() === 'izettle'
                    ? i18n('EOD.Card')
                    : name.replace('_', ' ').toLowerCase()}
                </Text>
                <Text>{method.count}</Text>
                <Text>{format(method.amount)}</Text>
              </Row>
            ))}
            <div>
              {_.map(reportToShow.paymentMethodTotalsPerStaff, (obj, key) => (
                <div key={key}>
                  <HeaderRow>
                    <Text>
                      {i18n('Report.PaymentMethods')} ({getStaffName(key, staffs)})
                      <span>
                        <Text>
                          <Paragraph level={5}>{i18n('ZReport.TurnoverPayments')}</Paragraph>
                        </Text>
                      </span>
                    </Text>
                    <Text>{i18n('General.Quantity')}</Text>
                    <Text>{i18n('General.Sum')}</Text>
                  </HeaderRow>
                  {_.map(obj, (method, name) => (
                    <Row key={name}>
                      <Text>
                        {name.toLowerCase() === 'adyen' || name.toLowerCase() === 'izettle'
                          ? i18n('EOD.Card')
                          : name.replace('_', ' ').toLowerCase()}
                      </Text>
                      <Text>{method.count}</Text>
                      <Text>{format(method.amount)}</Text>
                    </Row>
                  ))}
                </div>
              ))}
            </div>
            <HeaderRow>
              <Text>{i18n('Report.Tips')}</Text>
              <Text>{i18n('General.Quantity')}</Text>
              <Text>{i18n('General.Sum')}</Text>
            </HeaderRow>
            <Row>
              <Text>{i18n('Report.Tips')}</Text>
              <Text>{reportToShow.numberOfTips}</Text>
              <Text>{format(reportToShow.totalTips)}</Text>
            </Row>
            <Row>
              <Text>{i18n('ZReport.TipRefunds')}</Text>
              <Text>{reportToShow.tipRefunds?.count ?? 0}</Text>
              <Text>{format(-reportToShow.tipRefunds?.amount || 0)}</Text>
            </Row>
            <HeaderRow>
              <Text>{i18n('Report.Refunds')}</Text>
              <Text>{i18n('General.Quantity')}</Text>
              <Text>{i18n('General.Sum')}</Text>
            </HeaderRow>
            <Row>
              <Text>{i18n('Report.Refunds')}</Text>
              <Text>{reportToShow.totalRefunds?.count}</Text>
              <Text>{format(-reportToShow.totalRefunds?.amount)}</Text>
            </Row>

            {openCheques && (
              <>
                {' '}
                <HeaderRow>
                  <Text>{i18n('Report.OpenCheques')}</Text>
                  <Text>{i18n('General.Quantity')}</Text>
                  <Text>{i18n('General.Sum')}</Text>
                </HeaderRow>
                <Row>
                  <Text>{i18n('Report.OpenCheques')}</Text>
                  <Text>{openCheques.count}</Text>
                  <Text>{format(openCheques.amount)}</Text>
                </Row>
              </>
            )}

            <HeaderRow>
              <Text>{i18n('Report.Expenses')}</Text>
              <Text>{i18n('General.Sum')}</Text>
            </HeaderRow>
            <Row>
              <Text>{i18n('Report.Expenses')}</Text>
              <Text>{format(reportToShow.totalExpenses)}</Text>
            </Row>

            {cashRegister && (
              <>
                <HeaderRow>
                  <Text>{i18n('Report.CashRegister')}</Text>
                  <Text>{i18n('General.Sum')}</Text>
                </HeaderRow>
                <Row>
                  <Text>{i18n('Report.CashRegisterIn')}</Text>
                  <Text>{format(cashRegister.cashInitial)}</Text>
                </Row>
                {/* {Boolean(reportToShow.paymentMethodTotals?.CASH?.amount) && (
                  <>
                    <Row>
                      <Text>{i18n('ZReport.CashRegister.CashSales')}</Text>
                      <Text>+{format(reportToShow.paymentMethodTotals?.CASH?.amount)}</Text>
                    </Row>
                    {cashRegister.cashSalesIncTips -
                      reportToShow.paymentMethodTotals?.CASH?.amount >
                      0 && (
                      <Row>
                        <Text>{i18n('ZReport.CashRegister.CashTips')}</Text>
                        <Text>
                          +
                          {format(
                            cashRegister.cashSalesIncTips -
                              reportToShow.paymentMethodTotals?.CASH?.amount,
                          )}
                        </Text>
                      </Row>
                    )}
                  </>
                )}
                {cashRegister.cashDeposits > 0 && (
                  <Row>
                    <Text>{i18n('ZReport.CashRegister.CashDeposits')}</Text>
                    <Text>+{format(cashRegister.cashDeposits)}</Text>
                  </Row>
                )}
                {cashRegister.cashWithdrawals > 0 && (
                  <Row>
                    <Text>{i18n('ZReport.CashRegister.CashWithdrawals')}</Text>
                    <Text>-{format(cashRegister.cashWithdrawals)}</Text>
                  </Row>
                )} */}
                <Row>
                  <Text>
                    <strong>{i18n('Report.CashRegisterOut')}</strong>
                  </Text>
                  <Text>
                    <strong>{format(cashRegister.cashFinal)}</strong>
                  </Text>
                </Row>
              </>
            )}

            <HeaderRow>
              <Text>{i18n('CashManagement.Payouts')}</Text>
              <Text>{i18n('General.Sum')}</Text>
            </HeaderRow>
            {Object.keys(payouts)?.length > 0 ? (
              Object.keys(payouts)?.map((payout: any) => {
                return (
                  <Row>
                    <Text>
                      {payout === 'DRAWER' ? i18n('CashManagement.CashRegister') : payout}
                    </Text>
                    <Text>{format(-payouts[payout])}</Text>
                  </Row>
                );
              })
            ) : (
              <Row>
                <Text>-</Text>
                <Text>{format(0)}</Text>
              </Row>
            )}

            <HeaderRow>
              <Text>{i18n('Report.Receipts')}</Text>
              <Text>{i18n('General.Quantity')}</Text>
              <Text>{i18n('General.Sum')}</Text>
            </HeaderRow>
            <Row>
              <Text>{i18n('Report.RegisterReceipts')}</Text>
              <Text>{reportToShow.printedReceipts}</Text>
              <Text>-</Text>
            </Row>
            <Row>
              <Text>{i18n('Report.ReceiptCopies')}</Text>
              <Text>{reportToShow.emailedReceiptCopies + reportToShow.printedReceiptCopies}</Text>
              <Text>{format(reportToShow.totalReceiptCopiesAmount)}</Text>
            </Row>
            <Row>
              <Text>{i18n('ZReport.TrainingReceipts')}</Text>
              <Text>0</Text>
              <Text>{format(0)}</Text>
            </Row>
            <Row>
              <Text>{i18n('ZReport.DeliveryReceipts')}</Text>
              <Text>0</Text>
              <Text>{format(0)}</Text>
            </Row>
            <Row>
              <Text>{i18n('ZReport.ProvisionalReceipts')}</Text>
              <Text>{reportToShow.provisionalReceipts.count}</Text>
              <Text>{format(reportToShow.provisionalReceipts.amount)}</Text>
            </Row>
            <Row>
              <Text>{i18n('ZReport.RefundReceipts')}</Text>
              <Text>{reportToShow.refundReceipts.count}</Text>
              <Text>{format(-reportToShow.refundReceipts.amount)}</Text>
            </Row>

            <HeaderRow>
              <Text>{i18n('ZReport.Statistics')}</Text>
              <Text>{i18n('General.Quantity')}</Text>
              <Text>{i18n('General.Sum')}</Text>
            </HeaderRow>
            <Row>
              <Text>{i18n('ZReport.AmountPerTransaction')}</Text>
              <Text>{reportToShow.numberOfOrders}</Text>
              <Text>
                {format((reportToShow.totalSales ?? 0) / (reportToShow.numberOfOrders ?? 1))}
              </Text>
            </Row>
            <Row>
              <Text>{i18n('ZReport.TipPerTransaction')}</Text>
              <Text>{reportToShow.numberOfTips}</Text>
              <Text>
                {format((reportToShow.totalTips ?? 0) / (reportToShow.numberOfOrders ?? 1))}
              </Text>
            </Row>
            <Row>
              <Text>{i18n('ZReport.PriceLookUps')}</Text>
              <Text>0</Text>
              <Text>{format(0)}</Text>
            </Row>
            <Row>
              <Text>{i18n('ZReport.CashDrawerOpenings')}</Text>
              <Text>{reportToShow.totalCashBoxOpenings}</Text>
              <Text>-</Text>
            </Row>
            <Row>
              <Text>{i18n('ZReport.LinesEntered')}</Text>
              <Text>-</Text>
              <Text>{format(linesEntered)}</Text>
            </Row>
            <Row>
              <Text>{i18n('ZReport.Corrections')}</Text>
              <Text>{reportToShow?.lineCorrections['REMOVE_ITEM']?.count ?? 0}</Text>
              <Text>{format(-(reportToShow?.lineCorrections['REMOVE_ITEM']?.amount ?? 0))}</Text>
            </Row>
            <Row>
              <Text>{i18n('ZReport.NonCompletedCheques')}</Text>
              <Text>{reportToShow?.lineCorrections['REMOVE_CHEQUE']?.count ?? 0}</Text>
              <Text>{format(-(reportToShow?.lineCorrections['REMOVE_CHEQUE']?.amount ?? 0))}</Text>
            </Row>
            <Row>
              <Text>{i18n('ZReport.Discounts')}</Text>
              <Text>{reportToShow.discountsTotal}</Text>
              <Text>{format(-reportToShow.discountsAmount)}</Text>
            </Row>
            <Row>
              <Text>{i18n('ZReport.Turnover')}</Text>
              <Text>-</Text>
              <Text>{format(turnover)}</Text>
            </Row>

            <HeaderRow>
              <Text>{i18n('Report.AllTimeSales')}</Text>
              <Text>{i18n('General.Sum')}</Text>
            </HeaderRow>
            <Row>
              <Text>{i18n('Report.TotalSalesVat')}</Text>
              <Text>{format(reportToShow.allTimeSales)}</Text>
            </Row>
            <Row>
              <Text>{i18n('Report.SumRefunds')}</Text>
              <Text>{format(-reportToShow.allTimeRefunds)}</Text>
            </Row>
            <Row>
              <Text>{i18n('Report.AllTimeSum')}</Text>
              <Text>{format(reportToShow.allTimeSales - reportToShow.allTimeRefunds)}</Text>
            </Row>
          </Table>

          <OrgInfo>
            <p>{i18n('ZReport.Manufacturer')}: Baemingo AB</p>
            <p>
              {i18n('ZReport.ControlUnit')}: {storage.taxComplianceControlUnitSerial}
            </p>
            <p>
              {i18n('General.Company')}: {companyName}
            </p>
            <p>
              {i18n('General.OrgNr')}: {orgId}
            </p>
          </OrgInfo>
          {data && <PrintButton {...printButton}>{i18n('Report.PrintReport')}</PrintButton>}
        </TableWrapper>
      )}
    </Wrapper>
  );
};

export default NorwayView;
